import { motion } from "framer-motion";
import { useLottie } from "lottie-react";
import React, { useEffect, useState } from "react";
import { framerAnim } from "utilities/framer";
import AnimatedText from "../Text/AnimatedText";
import styles from "./Curve.module.scss";

type CurveProps = {
  children: React.ReactNode;
  name?: string;
  color?: string;
  animationData: string;
};

const style: React.CSSProperties = {
  position: "absolute",
  left: "0px",
  top: "0px",
  width: "100%",
  height: "100%",
  zIndex: 101,
};

const BumperAnimation = ({ animationData }: { animationData: string }) => {
  const options = {
    animationData: animationData,
    loop: false,
    autoPlay: true,
  };

  const { View } = useLottie(options, style);

  return View;
};

export default function Curve({
  children,
  name,
  color,
  animationData,
}: CurveProps) {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const [transition] = useState(new Audio("/audio/navigation/transition.wav"));

  useEffect(() => {
    transition.play();
  }, [animationData]);

  return (
    <motion.div className={styles.curve}>
      {width > 0 && (
        <SVG
          animationData={animationData}
          color={color}
          width={width}
          height={height}
          name={name}
        />
      )}
      {children}
    </motion.div>
  );
}

const SVG = ({
  width,
  height,
  name = "Geluk en leven",
  color,
  animationData,
}: {
  width: number;
  height: number;
  name?: string;
  color?: string;
  animationData: string;
}) => {
  // Adjust the width to account for the extra 300px on either side of the curve
  const adjustedWidth = width + 600;

  const initialPath = `
        M300 0
        Q0 ${height / 2} 300 ${height + 300}
        L${adjustedWidth - 300} ${height}
        Q${adjustedWidth} ${height / 2} ${adjustedWidth - 300} 0
        L300 0
    `;

  const targetPath = `
        M300 0
        Q0 ${height / 2} 300 ${height + 300}
        L${adjustedWidth - 600} ${height}
        Q${adjustedWidth - 600} ${height / 2} ${adjustedWidth - 600} 0
        L300 0
    `;

  const curve = {
    initial: {
      d: initialPath,
    },
    animate: {
      d: targetPath,
      transition: {
        duration: 3,
        delay: 1,
        ease: [0.76, 0, 0.24, 1],
      },
    },
    exit: {
      d: initialPath,
    },
  };

  const slide = {
    initial: {
      left: "-300px",
    },
    animate: {
      left: "-100dvw",
      transition: {
        duration: 3,
        delay: 1,
        ease: [0.76, 0, 0.24, 1],
      },
      transitionEnd: {
        left: "100dvw",
      },
    },
    exit: {
      left: "-300px",
      transition: {
        duration: 0.85,
        delay: 0.3,
        ease: [0.76, 0, 0.24, 1],
      },
    },
  };

  const textWipe = {
    initial: { opacity: 1 },
    animate: {
      opacity: 0,
      transition: {
        duration: 2.85,
        delay: 1,
        ease: [0.76, 0, 0.24, 1],
      },
    },
  };

  return (
    <>
      <motion.div {...framerAnim(textWipe)} className={styles.bumperText}>
        <AnimatedText text={name} />
      </motion.div>
      {animationData && <BumperAnimation animationData={animationData} />}
      <motion.svg {...framerAnim(slide)} className={styles.svg}>
        <motion.path fill={color} {...framerAnim(curve)} d={initialPath} />
      </motion.svg>
    </>
  );
};
